import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { IPaginatedResponse } from 'types/api/general.interface'
import {
  IShiftProgramDetailsResponse,
  IShiftProgramResponse,
  IPostPatchShiftProgramRequest,
} from 'types/api/operationsShiftProgramsApi.interface'
import {
  IShiftProgramsSliceInitalState,
  IShiftProgramState,
} from 'types/operationShiftPrograms.interface'
import { baseUrl } from 'utils/axios'
import { handleErrors } from 'utils/helpers'

const initialState: IShiftProgramsSliceInitalState = {
  isLoading: false,
  data: [],
  totalCount: 0,
  totalPages: 0,
  singleShiftProgram: null,
  shiftProgramDetails: null,
}

const mapApiFrequencyTypeToState = (
  frequencyType?: string,
  recurrenceType?: 1 | 2 | 3 | 4,
): 'everyShift' | 'daily' | 'weekly' | 'custom' => {
  if (recurrenceType === 2) return 'daily'
  if (recurrenceType === 3) return 'weekly'
  if (recurrenceType === 4) return 'custom'
  if (recurrenceType === 1) return 'everyShift'
  if (frequencyType === 'DAILY') return 'daily'
  if (frequencyType === 'WEEKLY') return 'weekly'
  if (frequencyType === 'CUSTOM') return 'custom'
  return 'everyShift'
}

const mapStateFrequencyTypeToRecurrenceType = (
  frequencyType: 'everyShift' | 'daily' | 'weekly' | 'custom',
): 1 | 2 | 3 | 4 => {
  if (frequencyType === 'daily') return 2
  if (frequencyType === 'weekly') return 3
  if (frequencyType === 'custom') return 4
  return 1
}

export const getShiftPrograms = createAsyncThunk(
  'shiftPrograms/getShiftPrograms',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IShiftProgramResponse>> = await baseUrl.get(
        'aim-operations/shift-program',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getShiftProgramDetails = createAsyncThunk(
  'shiftPrograms/getShiftProgramDetails',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<IShiftProgramDetailsResponse> = await baseUrl.get(
        `aim-operations/shift-program/${id}/details`,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const runShiftProgramNow = createAsyncThunk(
  'shiftPrograms/runShiftProgramNow',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<{ message: string }> = await baseUrl.post(
        `aim-operations/shift-program/${id}/run-now`,
      )
      await thunkAPI.dispatch(getShiftProgramDetails(id))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleShiftProgram = createAsyncThunk(
  'shiftPrograms/getSingleShiftProgram',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<IShiftProgramResponse> = await baseUrl.get(
        `aim-operations/shift-program/${id}`,
      )
      const rawBindings = ((resp.data as unknown as { bindings?: any[] }).bindings ??
        (resp.data as unknown as { bindings?: any[] }).bindings ??
        []) as any[]
      const state: IShiftProgramState = {
        name: resp.data.name,
        customIntervalDays: resp.data.customIntervalDays ?? null,
        notes: resp.data.notes,
        site: resp.data.site
          ? {
              label: resp.data.site.name,
              value: resp.data.site.id,
            }
          : null,
        department: resp.data.department
          ? { label: resp.data.department.name, value: resp.data.department.id }
          : null,
        teams:
          resp.data.teams?.map((team) => ({
            label: team.name,
            value: team.id,
          })) ?? [],
        frequencyType: mapApiFrequencyTypeToState(
          resp.data.frequencyType,
          resp.data.recurrenceType,
        ),
        daysOfWeek: resp.data.weeklyDays ?? resp.data.daysOfWeek ?? [],
        shifts:
          resp.data.shifts?.map((shift) => ({
            label: shift.name,
            value: shift.id,
          })) ?? [],
        bindings: rawBindings.map((binding) => ({
          taskTemplate: binding.taskTemplate
            ? {
                label: binding.taskTemplate.name,
                value: binding.taskTemplate.id,
              }
            : null,
          locations:
            (
              binding.locations ??
              (binding.location ? [binding.location] : binding.locationIds ?? [])
            ).map((location) => ({
              label:
                typeof location === 'number'
                  ? location.toString()
                  : location.name ?? location.label ?? location.id?.toString(),
              value: typeof location === 'number' ? location : location.id ?? location.value,
            })) ?? [],
          workersPerLocation: binding.workersPerLocation,
          shifts:
            (binding.shifts ?? []).map((shift) => ({
              label:
                typeof shift === 'number'
                  ? shift.toString()
                  : shift.name ?? shift.label ?? shift.id?.toString(),
              value: typeof shift === 'number' ? shift : shift.id ?? shift.value,
            })) ?? [],
        })),
      }
      return state
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addShiftProgram = createAsyncThunk(
  'shiftPrograms/addShiftProgram',
  async (state: IShiftProgramState, thunkAPI) => {
    const req: IPostPatchShiftProgramRequest = {
      name: state.name,
      notes: state.notes,
      siteId: state.site?.value,
      departmentId: state.department?.value,
      teamIds: state.teams?.map((team) => team.value) ?? [],
      recurrenceType: mapStateFrequencyTypeToRecurrenceType(state.frequencyType),
      weeklyDays: state.frequencyType === 'weekly' ? state.daysOfWeek : undefined,
      customIntervalDays: state.frequencyType === 'custom' ? state.customIntervalDays : undefined,
      shiftIds: state.shifts?.map((shift) => shift?.value) ?? [],
      bindings:
        state.bindings?.map((binding) => ({
          taskTemplateId: binding.taskTemplate?.value,
          locationIds: binding.locations?.map((location) => location.value) ?? [],
        })) ?? [],
    }

    try {
      const resp: AxiosResponse = await baseUrl.post('aim-operations/shift-program', req)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editShiftProgram = createAsyncThunk(
  'shiftPrograms/editShiftProgram',
  async ({ id, state }: { id: string; state: IShiftProgramState }, thunkAPI) => {
    const req: IPostPatchShiftProgramRequest = {
      name: state.name,
      notes: state.notes,
      siteId: state.site?.value,
      teamIds: state.teams?.map((team) => team.value) ?? [],
      departmentId: state.department?.value,
      recurrenceType: mapStateFrequencyTypeToRecurrenceType(state.frequencyType),
      weeklyDays: state.frequencyType === 'weekly' ? state.daysOfWeek : undefined,
      customIntervalDays: state.frequencyType === 'custom' ? state.customIntervalDays : undefined,
      shiftIds: state.shifts?.map((shift) => shift?.value) ?? [],
      bindings:
        state.bindings?.map((binding) => ({
          taskTemplateId: binding.taskTemplate?.value,
          locationIds: binding.locations?.map((location) => location.value) ?? [],
        })) ?? [],
    }

    try {
      const resp: AxiosResponse = await baseUrl.patch(`aim-operations/shift-program/${id}`, req)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const shiftProgramsSlice = createSlice({
  name: 'shiftPrograms',
  initialState,
  reducers: {
    clearShiftPrograms: (state) => {
      state.data = []
      state.totalCount = 0
      state.totalPages = 0
    },
    clearSingleShiftProgram: (state) => {
      state.singleShiftProgram = null
    },
    clearShiftProgramDetails: (state) => {
      state.shiftProgramDetails = null
    },
  },
  extraReducers: {
    [getShiftPrograms.pending.type]: (state) => {
      state.isLoading = true
    },
    [getShiftPrograms.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IShiftProgramResponse> },
    ) => {
      state.isLoading = false
      state.data = payload.data
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
    },
    [getShiftPrograms.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getShiftProgramDetails.pending.type]: (state) => {
      state.isLoading = true
    },
    [getShiftProgramDetails.fulfilled.type]: (
      state,
      { payload }: { payload: IShiftProgramDetailsResponse },
    ) => {
      state.isLoading = false
      state.shiftProgramDetails = payload
    },
    [getShiftProgramDetails.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [runShiftProgramNow.pending.type]: (state) => {
      state.isLoading = true
    },
    [runShiftProgramNow.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Shift program run queued successfully')
    },
    [runShiftProgramNow.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleShiftProgram.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleShiftProgram.fulfilled.type]: (
      state,
      { payload }: { payload: IShiftProgramState },
    ) => {
      state.isLoading = false
      state.singleShiftProgram = payload
    },
    [getSingleShiftProgram.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addShiftProgram.pending.type]: (state) => {
      state.isLoading = true
    },
    [addShiftProgram.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Shift program added successfully')
    },
    [addShiftProgram.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editShiftProgram.pending.type]: (state) => {
      state.isLoading = true
    },
    [editShiftProgram.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Shift program edited successfully')
    },
    [editShiftProgram.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearShiftPrograms, clearSingleShiftProgram, clearShiftProgramDetails } =
  shiftProgramsSlice.actions

export default shiftProgramsSlice.reducer
